"use strict";

var _interopRequireDefault = require("@babel/runtime/helpers/interopRequireDefault");
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.SelectableAddress = void 0;
var _icons = require("@ori-ui/icons");
var _mui = require("@ori-ui/mui");
var _checkoutUi = require("@ori/checkout-ui");
var _testidGenerator = _interopRequireDefault(require("@ori/testid-generator"));
var _react = require("react");
var _addressFormContext = require("../../contexts/addressFormContext");
var _hooks = require("../../hooks");
var _utils = require("../../utils");
var _DeliveryBox = require("../DeliveryBox");
var _useDeliveryItem = require("../DeliveryBox/useDeliveryItem");
var _Availability = require("../SelectPickupPointDialog/components/Availability");
var _styles = require("../SelectPickupPointDialog/components/PickupPointCard/styles");
var _Price = require("../SelectPickupPointDialog/components/Price");
var _shared = require("../shared");
var _AddressFormSkeleton = require("./AddressFormSkeleton");
var _styles2 = require("./styles");
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) {
  if ("function" != typeof WeakMap) return null;
  var r = new WeakMap(),
    t = new WeakMap();
  return (_getRequireWildcardCache = function (e) {
    return e ? t : r;
  })(e);
}
function _interopRequireWildcard(e, r) {
  if (!r && e && e.__esModule) return e;
  if (null === e || "object" != typeof e && "function" != typeof e) return {
    default: e
  };
  var t = _getRequireWildcardCache(r);
  if (t && t.has(e)) return t.get(e);
  var n = {
      __proto__: null
    },
    a = Object.defineProperty && Object.getOwnPropertyDescriptor;
  for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) {
    var i = a ? Object.getOwnPropertyDescriptor(e, u) : null;
    i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u];
  }
  return n.default = e, t && t.set(e, n), n;
}
const AddressForm = /*#__PURE__*/(0, _react.lazy)(async () => ({
  default: (await Promise.resolve().then(() => _interopRequireWildcard(require('@ori/address-form')))).DeliveryAddressForm
}));
/** Helper function so that linter doesn't scream about complexity over 20 in the main hook ;) */
function getSelectableCardProps({
  activateDeliveryItem,
  deleteButtonText,
  editButtonText,
  hintMessages,
  invalidAddressText,
  isActive,
  isAddressDeletable,
  isAddressEditable,
  isAddressFormOpen,
  isDisabled,
  isHomeDelivery,
  isInvalid,
  isRefreshPending,
  isSelected,
  isShippingJoker,
  isTopRowVisible,
  onDeleteAddressClick,
  onEditAddressClick,
  onShippingOfferBannerClick,
  shippingOfferBannerText,
  testId
}) {
  const disabled = isDisabled || isInvalid || isRefreshPending || isAddressFormOpen;
  const usedHintMessages = [];
  if (isInvalid) {
    usedHintMessages.push(invalidAddressText);
  }
  if (isSelected && !isHomeDelivery) {
    usedHintMessages.push(...hintMessages);
  }
  const result = {
    // disabling for invalid (non-serviceable) addresses
    // or when a new delivery channel is being applied
    disabled,
    indicator: isHomeDelivery ? 'radio' : 'none',
    // selecting the item on click - only in home delivery selection, suggestions list for now
    onClick: isHomeDelivery ? activateDeliveryItem : undefined,
    // invalid address should never be selected
    selected: isSelected,
    testId,
    // hints & shipping offer banner, optional button section top alignment
    ...(0, _shared.getAdditionalSelectableCardProps)({
      isActive: isSelected,
      isAlignedToTop: !isTopRowVisible,
      hintMessages: usedHintMessages,
      isShippingJoker: isShippingJoker && !isHomeDelivery,
      onShippingOfferBannerClick,
      shippingOfferBannerText
    }),
    // edit & delete buttons for home address list items
    ...(isAddressDeletable && !isActive && {
      deleteButton: {
        disabled,
        onClick: onDeleteAddressClick,
        text: deleteButtonText
      }
    }),
    ...(isAddressEditable && {
      editButton: {
        disabled,
        onClick: onEditAddressClick,
        text: editButtonText
      }
    })
  };
  return result;
}

/**
 * Used for flat selection UI to display selected delivery instead of earlier-used `DeliveryBox`.
 *
 * For the pickup point variant, the hint messages, delivery fee, estimated delivery date, and shipping offer
 * are displayed directly in the same component.
 *
 * For home delivery variant, the above mentioned UI elements are displayed in a separate courier selection component.
 */
const SelectableAddress = ({
  isDisabled = false,
  ...props
}) => {
  const {
    formatMessage,
    translations
  } = (0, _hooks.useTranslations)();
  const {
    activateDeliveryItem,
    addressWrapperProps,
    courier,
    deliveryItem,
    hintMessages,
    isActive,
    isAddressDeletable = false,
    isAddressEditable = false,
    isHomeDelivery,
    isInvalid,
    isRefreshPending = false,
    onDeleteAddressClick,
    onEditAddressClick,
    onShippingOfferBannerClick,
    testId: rawTestId
  } = (0, _useDeliveryItem.useDeliveryItem)(props);
  const {
    getTestId
  } = (0, _testidGenerator.default)();
  const testId = getTestId(rawTestId);
  const {
    addressFormProps,
    isAddressFormOpen,
    lastAddressId
  } = (0, _addressFormContext.useAddressForm)();
  const {
    addressId,
    deliveryMethod,
    isShippingJoker,
    outOfStockQuantity,
    outOfStockProducts,
    telephoneNumber,
    workingHours
  } = deliveryItem;
  const {
    deliveryFee,
    originalDeliveryFee,
    estimatedDelivery
  } = courier;
  const deliveryMethodTranslationKey = (0, _utils.getDeliveryMethodTranslationKey)(deliveryMethod);
  const isInilineAddressFormVisible = isAddressEditable && isAddressFormOpen && lastAddressId === addressId;
  if (isInilineAddressFormVisible) {
    return /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles2.InlineAddressFormWrapper, {
      children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_react.Suspense, {
        fallback: /*#__PURE__*/(0, _jsxRuntime.jsx)(_AddressFormSkeleton.AddressFormSkeleton, {}),
        children: /*#__PURE__*/(0, _jsxRuntime.jsx)(AddressForm, {
          ...addressFormProps
        })
      })
    });
  }

  // latest agreement with designer is to hide the address list items completely when address form is open
  if (isAddressFormOpen) {
    return null;
  }
  const deleteButtonText = formatMessage('remove');
  const deliveryMethodText = translations[deliveryMethodTranslationKey];
  const editButtonText = formatMessage('edit');
  const invalidAddressText = formatMessage('notSupportedForDelivery');
  const shippingOfferBannerText = formatMessage('buyProductAndGetFreeDelivery');
  const isSelected = isActive && !isInvalid;
  const isTopRowVisible = !isHomeDelivery && (isShippingJoker || Boolean(deliveryMethodText));
  const selectableCardProps = getSelectableCardProps({
    activateDeliveryItem,
    deleteButtonText,
    editButtonText,
    hintMessages,
    invalidAddressText,
    isActive,
    isAddressDeletable,
    isAddressEditable,
    isAddressFormOpen,
    isDisabled,
    isHomeDelivery,
    isInvalid,
    isShippingJoker,
    isRefreshPending,
    isSelected,
    isTopRowVisible,
    onDeleteAddressClick,
    onEditAddressClick,
    onShippingOfferBannerClick,
    shippingOfferBannerText,
    testId
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_checkoutUi.SelectableCard, {
    ...selectableCardProps,
    children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_shared.SelectableCardContentBiggerSpacing, {
      children: [isTopRowVisible ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_shared.TopRowWrapper, {
        children: [deliveryMethodText ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_shared.InfoChip, {
          label: deliveryMethodText,
          variant: "tag"
        }) : null, isShippingJoker ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Chip, {
          color: "offer",
          label: formatMessage('freeShippingOffer'),
          variant: "tag"
        }) : null]
      }) : null, /*#__PURE__*/(0, _jsxRuntime.jsxs)(_shared.ContentRowWrapper, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_DeliveryBox.AddressInfo, {
          ...addressWrapperProps,
          isFlatSelection: true
        }), isHomeDelivery ? null : /*#__PURE__*/(0, _jsxRuntime.jsx)(_Price.Price, {
          originalShippingFee: originalDeliveryFee,
          shippingFee: deliveryFee
        })]
      }), isSelected && !isHomeDelivery ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_jsxRuntime.Fragment, {
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_shared.ContentRowWrapper, {
          children: /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.DateAndAvailability, {
            children: [estimatedDelivery ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_mui.Typography, {
              children: estimatedDelivery
            }) : null, /*#__PURE__*/(0, _jsxRuntime.jsx)(_Availability.Availability, {
              outOfStockQuantity: (0, _utils.getOutOfStockQuantity)({
                outOfStockQuantity,
                outOfStockProducts
              })
            })]
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_shared.ContentWithSeparator, {
          children: [workingHours ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.InfoLine, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Clock, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.InfoLineText, {
              children: workingHours
            })]
          }) : null, telephoneNumber ? /*#__PURE__*/(0, _jsxRuntime.jsxs)(_styles.InfoLine, {
            children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_icons.Telephone, {}), /*#__PURE__*/(0, _jsxRuntime.jsx)(_styles.InfoLineText, {
              children: telephoneNumber
            })]
          }) : null]
        })]
      }) : null]
    })
  });
};
exports.SelectableAddress = SelectableAddress;